import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hasRole, useApi, useApp } from "../../context/AppContext";

// Registering, editing and revoking a cabinet, and rolling its gallery back
// (docs/tasks/35): moved here from the retired Devices and Deployment pages.

const BOARDS = [
  ["jetson-orin-nano", "Jetson Orin Nano"],
  ["raspberry-pi-5", "Raspberry Pi 5"],
  ["raspberry-pi-4", "Raspberry Pi 4"],
  ["emulator", "Emulator (virtual cabinet: the cloud runs the visit)"],
];

export function RegisterCabinet({ onDone }) {
  const api = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ name: "", hardware: "jetson-orin-nano", location: "", notes: "", type: "" });
  const [made, setMade] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.listCabinetTypes().then(setTypes).catch(() => {}); }, [api, open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      const d = await api.registerDevice({ name: form.name.trim(), hardware: form.hardware, location: form.location || null, notes: form.notes || null });
      if (form.type) await api.setDeviceCabinetType(d.id, form.type).catch((ex) => setErr(`Registered, but the type was not set: ${ex.message || ex}`));
      setMade(d); setOpen(false); setForm({ name: "", hardware: "jetson-orin-nano", location: "", notes: "", type: "" });
      onDone?.();
    } catch (ex) { setErr(String(ex.message || ex)); } finally { setBusy(false); }
  };
  return (
    <>
      {!open && <button className="btn btn-secondary" onClick={() => setOpen(true)}>Register a cabinet</button>}
      {made && (
        <div className="card w-full border-amber-300 bg-amber-50 text-sm">
          <div className="font-semibold">{made.name} registered</div>
          <p className="text-xs text-slate-700">Copy the device key now: it is not shown again. It goes into the board's <code>/etc/ainaopen.env</code> (raspberry/instructions/01-deploy-pi5.md, jetson/scripts).</p>
          <pre className="mt-1 overflow-x-auto rounded bg-slate-900 p-2 text-xs text-slate-100">{`AINAOPEN_DEVICE_ID=${made.id}\nAINAOPEN_API_KEY=${made.device_key}`}</pre>
          <div className="mt-1 flex gap-3 text-xs">
            <button className="text-sky-700 hover:underline" onClick={() => navigate(`/cabinets/${made.id}`)}>open its page</button>
            <button className="text-slate-600 hover:underline" onClick={() => setMade(null)}>I have copied it</button>
          </div>
        </div>
      )}
      {open && (
        <form className="card grid w-full gap-2 md:grid-cols-5" onSubmit={submit}>
          <input className="input" required placeholder="name, e.g. VisionRP5" value={form.name} onChange={set("name")} />
          <select className="input" value={form.hardware} onChange={set("hardware")}>{BOARDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <select className="input" value={form.type} onChange={set("type")}>
            <option value="">cabinet type: later</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input className="input" placeholder="location" value={form.location} onChange={set("location")} />
          <div className="flex gap-2">
            <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? "…" : "Register"}</button>
            <button className="btn btn-secondary" type="button" onClick={() => setOpen(false)}>Cancel</button>
          </div>
          {err && <p className="text-xs text-rose-700 md:col-span-5">{err}</p>}
        </form>
      )}
    </>
  );
}

export function CabinetSettings({ deviceId, health, onChanged }) {
  const api = useApi();
  const { user } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState(null);
  const may = hasRole(user, "tenant_admin", "device_operator");
  useEffect(() => {
    api.getDevice(deviceId).then((d) => setForm({ name: d.name || "", location: d.location || "", notes: d.notes || "", revoked: d.revoked }))
      .catch(() => setForm(null));
  }, [api, deviceId]);
  if (!may || !form) return null;
  const save = async (e) => {
    e.preventDefault(); setMsg(null);
    try {
      await api.updateDevice(deviceId, { name: form.name.trim(), location: form.location || null, notes: form.notes || null });
      setMsg({ text: "Saved." }); onChanged?.();
    } catch (ex) { setMsg({ bad: true, text: String(ex.message || ex) }); }
  };
  const revoke = async () => {
    const typed = window.prompt(`Revoke ${form.name}? Its device key stops working at once and it cannot report or take commands. Type its name to confirm.`);
    if (typed !== form.name) return;
    try { await api.revokeDevice(deviceId); navigate("/"); } catch (ex) { setMsg({ bad: true, text: String(ex.message || ex) }); }
  };
  return (
    <form className="card space-y-2" onSubmit={save}>
      <h2 className="text-sm font-semibold">Settings</h2>
      <div className="grid gap-2 md:grid-cols-3">
        <label className="text-xs">Name<input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="text-xs">Location<input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></label>
        <label className="text-xs">Notes<input className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn btn-primary text-xs" type="submit">Save</button>
        {!form.revoked && <button className="btn btn-secondary text-xs text-rose-700" type="button" onClick={revoke}>Revoke this cabinet…</button>}
        {msg && <span className={`text-xs ${msg.bad ? "text-rose-700" : "text-emerald-700"}`}>{msg.text}</span>}
      </div>
    </form>
  );
}

// The gallery the cabinet ran before: POST …/rollback clears the assignment so it
// returns to its previous installed version.
export function GalleryRollback({ deviceId, health }) {
  const api = useApi();
  const { user } = useApp();
  const [msg, setMsg] = useState(null);
  if (!hasRole(user, "tenant_admin", "device_operator", "ml_operator")) return null;
  const r = health?.report || {};
  const atCabinet = r.door?.state === "open" || (r.session && r.session.state !== "closed");
  const go = async () => {
    if (atCabinet) { setMsg({ bad: true, text: "The door is open or a visit is running." }); return; }
    if (!window.confirm("Roll this cabinet's gallery back to the one it ran before? It switches between visits.")) return;
    try { await api.rollbackDevice(deviceId); setMsg({ text: "Rolled back; the cabinet switches on its next model check (up to 5 minutes)." }); }
    catch (ex) { setMsg({ bad: true, text: String(ex.message || ex) }); }
  };
  return (
    <span className="text-xs">
      <button className="text-sky-700 hover:underline" type="button" onClick={go}>roll the gallery back</button>
      {msg && <span className={`ml-2 ${msg.bad ? "text-rose-700" : "text-emerald-700"}`}>{msg.text}</span>}
    </span>
  );
}
